/**
 * Schneidet den Ratgeber-Text an seinen Ueberschriften, damit ein Mensch einen einzelnen
 * Abschnitt bearbeiten kann, ohne dass der Rest angefasst wird.
 *
 * Der Schnitt arbeitet auf dem MARKDOWN, nicht auf `ratgeber_struktur`: Die Struktur ist
 * eine verlustbehaftete Ableitung (Absaetze werden zusammengezogen, Leerzeilen fallen weg)
 * und taugt zum Wiederherstellen des Textes nicht. Sie liefert nur die Abschnitts-IDs.
 *
 * Ein <textarea> im Browser liefert Text mit CRLF-Zeilenenden, der Text aus n8n traegt LF.
 * Ohne Normalisierung waere nach der ersten Bearbeitung jedes Segment "veraendert" -- die
 * Byte-Gleichheit waere dahin, und der Ueberarbeitungs-Agent bekaeme faelschlich den ganzen
 * Ratgeber als "von Hand bearbeitet" gemeldet. Deshalb normalisiert jede Funktion hier
 * Zeilenenden auf `\n`, bevor sie schneidet, vergleicht oder zusammensetzt.
 */

import type { EntwurfSegmentEintrag } from '@awf/shared';

export interface Segment {
  index: number;
  ueberschrift: string;
  ebene: number;
  markdown: string;
}

const UEBERSCHRIFT = /^(#{1,6})\s+(.*)$/;

function normalisiereZeilenenden(text: string): string {
  return text.replace(/\r\n/g, '\n');
}

/**
 * Die Schnittregel von `schneide` fuer sich: je Zeile der Ueberschrift-Treffer, wenn dort ein
 * neuer Abschnitt beginnt, sonst `null`. Eigene Funktion, damit `abschnittsAnfaenge` (und
 * darueber `bloecke.ts`) exakt dieselbe Regel benutzt statt einer zweiten Kopie von Regex
 * und Codeblock-Erkennung (Task 9a). Nur Backtick-Fences schalten den Codeblock um; das
 * bleibt absichtlich so, weil jede Aenderung hier die Segmente bestehender Entwuerfe
 * verschoebe.
 */
function ueberschriftsZeilen(zeilen: readonly string[]): (RegExpExecArray | null)[] {
  let imCodeblock = false;
  return zeilen.map((zeile) => {
    if (zeile.trimStart().startsWith('```')) imCodeblock = !imCodeblock;
    return imCodeblock ? null : UEBERSCHRIFT.exec(zeile);
  });
}

/**
 * Offsets (im auf LF normalisierten Text) aller Zeilen, an denen `schneide` einen neuen
 * Abschnitt beginnen laesst -- 0 eingeschlossen, wenn der Text mit einer Ueberschrift beginnt.
 * `bloecke.ts` schneidet seine Regionen hier, damit kein Block ueber eine Abschnittsgrenze
 * reicht: `marked` sieht Ueberschriften nicht immer dort, wo diese Regel sie sieht (ein
 * HTML-Block ohne Leerzeile verschluckt die naechste Zeile, `~~~` ist fuer `marked` ein Fence).
 */
export function abschnittsAnfaenge(markdown: string): number[] {
  const zeilen = normalisiereZeilenenden(markdown).split('\n');
  const treffer = ueberschriftsZeilen(zeilen);
  const anfaenge: number[] = [];
  let position = 0;
  zeilen.forEach((zeile, i) => {
    if (treffer[i]) anfaenge.push(position);
    position += zeile.length + 1;
  });
  return anfaenge;
}

export function schneide(markdown: string): Segment[] {
  const zeilen = normalisiereZeilenenden(markdown).split('\n');
  const trefferJeZeile = ueberschriftsZeilen(zeilen);
  const segmente: Segment[] = [];
  let aktuell: string[] = [];
  let ueberschrift = '';
  let ebene = 0;

  const abschliessen = () => {
    // Vor der allerersten Ueberschrift gibt es noch keinen Vorspann zu sichern, wenn der
    // Text direkt mit einer Ueberschrift beginnt -- sonst entstuende ein leeres Phantom-
    // Segment und alle nachfolgenden Indizes verschoeben sich.
    if (aktuell.length === 0) return;
    segmente.push({
      index: segmente.length,
      ueberschrift,
      ebene,
      markdown: aktuell.join('\n'),
    });
  };

  for (const [i, zeile] of zeilen.entries()) {
    const treffer = trefferJeZeile[i];
    if (treffer) {
      abschliessen();
      aktuell = [zeile];
      ueberschrift = (treffer[2] ?? '').trim();
      ebene = (treffer[1] ?? '').length;
      continue;
    }
    aktuell.push(zeile);
  }
  abschliessen();

  return segmente;
}

/**
 * Leere Abschnitte (ein ganz entfernter Abschnitt, Teil B) fallen samt Trennzeile weg --
 * ihr Eintrag bleibt in der Liste stehen, damit Indizes und `herkunft` sich nicht
 * verschieben, aber er erzeugt keine zusaetzliche Leerzeile im Text.
 *
 * Fix-Runde 1, Minor 3: das gilt auch fuer ein FUEHRENDES leeres Segment 0 (eine Leerzeile
 * vor der ersten Ueberschrift, z. B. `'\n# A\n\nText'`) -- der Roundtrip ueber `schneide()`
 * und `zusammen()` verliert diese Leerzeile dann. Das ist gewollt, nicht nur ein Nebeneffekt
 * des Filters: eine fuehrende Leerzeile traegt keine sichtbare Information.
 */
export function zusammen(segmente: Segment[]): string {
  return segmente
    .map((s) => s.markdown)
    .filter((markdown) => markdown !== '')
    .join('\n');
}

export function ersetze(segmente: Segment[], index: number, neuesMarkdown: string): Segment[] {
  const normalisiert = normalisiereZeilenenden(neuesMarkdown);
  return segmente.map((s) => (s.index === index ? { ...s, markdown: normalisiert } : s));
}

/**
 * `ersetze()` aktualisiert bewusst nur `markdown` (siehe oben) -- `ueberschrift`/`ebene`
 * eines Segments sind deshalb nach einer Uebernahme potenziell veraltet, wenn die
 * Bearbeitung die Ueberschriftszeile selbst betraf. Statt die gespeicherten Felder
 * nachzuziehen (mit dem Risiko, Indizes durch ein Neu-Schneiden zu verschieben, falls die
 * Bearbeitung eine Ueberschrift hinzufuegt oder entfernt), liest diese Funktion die
 * aktuelle Ueberschrift direkt und bei Bedarf aus dem Markdown -- fuer alles, was sie nur
 * ANZEIGT (Aria-Label des Bearbeitungsfelds), nie fuer die Segmentidentitaet selbst.
 */
export function ersteUeberschrift(markdown: string): { ueberschrift: string; ebene: number } {
  const ersteZeile = normalisiereZeilenenden(markdown).split('\n')[0] ?? '';
  const treffer = UEBERSCHRIFT.exec(ersteZeile);
  return treffer
    ? { ueberschrift: (treffer[2] ?? '').trim(), ebene: (treffer[1] ?? '').length }
    : { ueberschrift: '', ebene: 0 };
}

/**
 * Schneidet einen ABWEICHEND gespeicherten Text (z. B. einen geladenen Entwurf) an genau
 * den Ueberschriften von `ursprung` -- nie an einer neuen (Ruling 20 im Lern-Ledger,
 * Task-13-Review HOCH-1). Ohne diese Einschraenkung wuerde `schneide()` einen Entwurf, der
 * zusaetzlich zur eigentlichen Bearbeitung eine Zwischenueberschrift einfuegt (z. B.
 * `### B2` innerhalb eines bearbeiteten Abschnitts), in MEHR Segmente zerlegen als
 * `ursprung` hat -- die Zuordnung Segment-Index -> Abschnitts-ID in `freigabe-page.tsx`
 * (`struktur.abschnitte[index]`) verschoebe sich dann, und der Ueberarbeitungs-Agent
 * bekaeme den FALSCHEN Abschnitt als "von Hand bearbeitet" gemeldet -- derselbe Schaden,
 * den die CRLF-Normalisierung oben abwehrt, nur durch eine andere Tuer. Waehrend einer
 * laufenden Bearbeitungssitzung passiert das nicht, weil `ersetze()` ausschliesslich
 * `markdown` tauscht und nie neu schneidet; diese Funktion bildet genau dieses Verhalten
 * fuer den Ladefall nach.
 *
 * STATUS SEIT RULING 34 (`schneideNachSegmentierung` unten): diese Ankersuche ist NICHT
 * mehr der reguläre Weg, um einen geladenen Entwurf wieder in Segmente zu zerlegen --
 * `freigabe-page.tsx` (`segmentierungEinlesen`) liest die Zuordnung stattdessen aus der
 * beim Speichern mitgefuehrten `segmentierung` und faellt auf DIESE Funktion nur noch
 * zurueck, wenn ein Entwurf gar keine `segmentierung` traegt (Altbestand von VOR diesem
 * Ruling) oder die gespeicherten Angaben nicht mehr zu seinem Text passen. Das Restrisiko
 * dieser Ankersuche bleibt fuer genau diesen Rueckfall bestehen und ist unten dokumentiert
 * (Dublette eines NICHT letzten Ankers, "Restrisiko, bewusst in Kauf genommen") -- ein
 * regulaer gespeicherter Entwurf durchlaeuft es seit Ruling 34 gar nicht mehr.
 *
 * Jeder Anker (die erste Zeile eines Segments aus `ursprung`, ab dem zweiten) wird
 * UNABHAENGIG von den anderen gesucht: Fehlt einer -- etwa weil seine Ueberschrift von
 * Hand umbenannt oder ganz entfernt wurde --, wird NUR sein Schnitt ausgelassen, sein
 * Ursprungs-Segment verschmilzt mit dem VORANGEHENDEN; die Suche nach allen WEITEREN
 * Ankern laeuft unbeeinflusst ab der zuletzt gefundenen Position weiter (Task-13-Re-Review
 * NEU-1). Die fruehere Fassung brach beim ERSTEN Fehltreffer komplett ab (ein einzelner,
 * in fester Reihenfolge fortschreitender Zeiger, der bei einem Fehltreffer nie mehr
 * vorruckte) -- schon eine einzige umbenannte Zwischenueberschrift liess dann JEDEN
 * weiteren Anker ungeprueft und die ganze Seite bei jedem Laden erneut auf EIN einziges
 * Bearbeitungsfeld zusammenfallen, obwohl Ueberschriften umbenennen Redaktionsalltag ist.
 *
 * `herkunft` im Rueckgabewert haelt fest, welche `ursprung`-Indizes in welchem
 * Ergebnis-Segment aufgegangen sind (normalerweise genau einer, mehr als einer nur an der
 * Verschmelzungsstelle). Die Aufruferin (`absenden` in `freigabe-page.tsx`) nutzt das, um
 * `bearbeiteteAbschnitte` auch in diesem Fall noch PRAEZISE statt pauschal zu melden --
 * der zweite Teil von Ruling 7 ("ab der Schnittstelle gilt alles Folgende als geaendert")
 * bezieht sich dann nur noch auf das jeweils verschmolzene Segment, nicht mehr auf den
 * gesamten Ratgeber.
 */
export interface SchnittAnUrsprung {
  segmente: Segment[];
  /** `herkunft[i]` sind die `ursprung`-Indizes, die in `segmente[i]` aufgegangen sind. */
  herkunft: number[][];
}

function bereich(von: number, bis: number): number[] {
  const ergebnis: number[] = [];
  for (let i = von; i <= bis; i += 1) ergebnis.push(i);
  return ergebnis;
}

export function schneideAnUrsprung(markdown: string, ursprung: Segment[]): SchnittAnUrsprung {
  const anker = ursprung
    .slice(1)
    .map((s) => normalisiereZeilenenden(s.markdown).split('\n')[0] ?? '');
  const zeilen = normalisiereZeilenenden(markdown).split('\n');

  // Codeblock-Grenzen VORAB fuer alle Zeilen ermitteln statt waehrend eines einzelnen
  // linearen Durchlaufs: Die Ankersuche unten springt je Anker neu an -- anders als beim
  // gewoehnlichen `schneide()` reicht ein einziger, stetig fortschreitender Zeiger hier
  // nicht mehr.
  const inCodeblock: boolean[] = [];
  {
    let flag = false;
    for (const zeile of zeilen) {
      if (zeile.trimStart().startsWith('```')) flag = !flag;
      inCodeblock.push(flag);
    }
  }

  // Reine Existenzpruefung (nicht die eigentliche Schnittstelle): kommen die Anker ab
  // `ankerIndex` alle -- irgendwo, in dieser Reihenfolge -- ab `ab` noch vor? Dient unten
  // nur der Plausibilitaet einer KANDIDATEN-Stelle, nicht der endgueltigen Positionswahl.
  function restPasstAb(ankerIndex: number, ab: number): boolean {
    let pos = ab;
    for (let k = ankerIndex; k < anker.length; k += 1) {
      let treffer = -1;
      for (let i = pos; i < zeilen.length; i += 1) {
        if (!inCodeblock[i] && zeilen[i] === anker[k]) {
          treffer = i;
          break;
        }
      }
      if (treffer === -1) return false;
      pos = treffer + 1;
    }
    return true;
  }

  // Zeilenindex, an dem jeweils ein neues Segment beginnt -- ein Eintrag je GEFUNDENEM
  // Anker (fehlt einer, gibt es dafuer keinen).
  const schnittpunkte: number[] = [];
  const herkunft: number[][] = [];
  let suchAb = 0;
  let vonUrsprungIndex = 0;
  for (let ankerIndex = 0; ankerIndex < anker.length; ankerIndex += 1) {
    // Task-13-Re-Review NEU-5: das ERSTE Vorkommen ab `suchAb` ist nicht mehr automatisch
    // die richtige Stelle -- fuegt ein Entwurf INNERHALB des vorherigen Abschnitts eine
    // Ueberschriftszeile ein, die woertlich mit dieser hier uebereinstimmt (eine Dublette),
    // waere das ein FALSCHER, zu frueher Treffer. Unter allen Vorkommen ab `suchAb` gilt
    // deshalb das LETZTE als Anker, fuer das die NOCH VERBLEIBENDEN Anker (falls es welche
    // gibt) anschliessend immer noch in der richtigen Reihenfolge auffindbar sind -- eine
    // Dublette liegt vor dem echten Anker und verletzt diese Plausibilitaet selbst nicht,
    // wird aber vom echten, SPAETEREN Treffer aus demselben Grund ueberboten. Bleibt gar
    // kein Treffer plausibel (z. B. beim letzten Anker, wo nichts mehr zu pruefen ist),
    // gilt ohnehin jedes Vorkommen als plausibel -- dann gewinnt ebenfalls das letzte.
    // Restrisiko, bewusst in Kauf genommen (Fix-Runde 6 bestaetigt, unveraendert seit
    // Fix-Runde 3/MITTEL-6): eine Dublette eines NICHT letzten Ankers faellt dieser Pruefung
    // nicht auf, wenn der echte, spaetere Anker vom selben Text zufaellig trotzdem noch
    // gefunden wird, obwohl DAZWISCHEN weitere Anker liegen muessten. RICHTUNG des Risikos:
    // dieselbe wie beim urspruenglichen Befund oben (NEU-5) -- der Schnitt landet dann zu
    // FRUEH, an der Dublette statt am echten Anker, und ein zwischen beiden liegender Edit
    // kann so unbemerkt bleiben (Unterberichtung, genau die Richtung, vor der Ruling 20
    // warnt). Zwei denkbare Gegenmassnahmen wurden geprueft und verworfen: eine umgekehrte
    // Praeferenz (erstes statt letztes plausibles Vorkommen) fuehrt exakt die urspruengliche
    // Unterberichtung wieder ein, die diese Funktion beheben soll; eine globale
    // Backtracking-Suche ueber alle Anker gleichzeitig waere der naechste Schritt, gefaehrdet
    // aber die an den 26 bereits geprueften Faellen bestaetigte Loesung ohne einen neuen,
    // konkreten Fehlerfall, der sie rechtfertigt.
    let gefunden = -1;
    let irgendeinTreffer = -1;
    for (let i = suchAb; i < zeilen.length; i += 1) {
      if (inCodeblock[i] || zeilen[i] !== anker[ankerIndex]) continue;
      if (irgendeinTreffer === -1) irgendeinTreffer = i;
      const plausibel = ankerIndex === anker.length - 1 || restPasstAb(ankerIndex + 1, i + 1);
      if (plausibel) gefunden = i;
    }
    if (gefunden === -1) gefunden = irgendeinTreffer; // keine plausible Stelle -- wie bisher die erste.
    if (gefunden === -1) continue; // Anker fehlt komplett -- auslassen, naechsten trotzdem suchen.
    schnittpunkte.push(gefunden);
    herkunft.push(bereich(vonUrsprungIndex, ankerIndex));
    suchAb = gefunden + 1;
    vonUrsprungIndex = ankerIndex + 1;
  }
  herkunft.push(bereich(vonUrsprungIndex, ursprung.length - 1));

  // Absichtlich KEINE Wache gegen ein leeres erstes Segment (anders als `abschliessen()`
  // in `schneide()` oben): Beginnt der Entwurf direkt mit dem ersten Anker, muss trotzdem
  // ein (dann leeres) Segment 0 entstehen, sonst passte `segmente.length` nicht mehr zur
  // Zahl der `herkunft`-Eintraege und die Indizes liefen gegeneinander (Task-13-Re-Review
  // NEU-6). Der leere Abschnitt ist ein bekannter, harmloser Rest dieser Abweichung.
  const segmente: Segment[] = [];
  let start = 0;
  for (const punkt of [...schnittpunkte, zeilen.length]) {
    const text = zeilen.slice(start, punkt).join('\n');
    segmente.push({ index: segmente.length, ...ersteUeberschrift(text), markdown: text });
    start = punkt;
  }

  return { segmente, herkunft };
}

/**
 * Ruling 34: loest den in `schneideAnUrsprung` dokumentierten Restfall STRUKTURELL statt
 * heuristisch. Statt Ueberschriften im gespeicherten Entwurf wiederzusuchen -- und dabei
 * auf eine Dublette hereinzufallen, wenn ein Abschnitt seine eigene Ueberschrift spaeter in
 * seinem eigenen Inhalt wiederholt --, schneidet diese Funktion exakt an den LAENGEN, die
 * beim Speichern (siehe `Auftrag['entwurf']['segmentierung']` in `packages/shared`)
 * zusammen mit dem jeweils bearbeiteten Segment selbst festgehalten wurden. Keine
 * Textsuche, keine Ankerpraeferenz, kein Restrisiko: Die Grenzen stammen vom selben Code,
 * der `segmente` beim Speichern auch tatsaechlich gehalten hat, nicht von einer spaeteren
 * Vermutung darueber.
 *
 * Liefert `null`, wenn die Laengen nicht mehr zu `markdown` passen (z. B. ein von Hand
 * veraendertes Firestore-Dokument) -- die Aufruferin (`freigabe-page.tsx`) faellt dann auf
 * `schneideAnUrsprung` als Rueckfallebene zurueck, dieselbe, die vor diesem Ruling der
 * einzige Weg war.
 */
export function schneideNachSegmentierung(
  markdown: string,
  segmentierung: EntwurfSegmentEintrag[],
): Segment[] | null {
  const text = normalisiereZeilenenden(markdown);
  const segmente: Segment[] = [];
  let start = 0;
  // Trennzeilen gibt es nur zwischen zwei NICHT leeren Abschnitten (siehe `zusammen`).
  let schonInhalt = false;
  for (const eintrag of segmentierung) {
    if (!eintrag || !Number.isInteger(eintrag.laenge) || eintrag.laenge < 0) return null;
    if (eintrag.laenge === 0) {
      segmente.push({ index: segmente.length, ueberschrift: '', ebene: 0, markdown: '' });
      continue;
    }
    if (schonInhalt) {
      if (text[start] !== '\n') return null;
      start += 1;
    }
    const ende = start + eintrag.laenge;
    if (ende > text.length) return null;
    const stueck = text.slice(start, ende);
    segmente.push({ index: segmente.length, ...ersteUeberschrift(stueck), markdown: stueck });
    start = ende;
    schonInhalt = true;
  }
  if (start !== text.length) return null;
  return segmente;
}
